import {SortingState} from '@tanstack/table-core';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import {MRT_ColumnDef, MRT_Row} from 'material-react-table';
import React, {useEffect, useState} from 'react';
import {useShallow} from 'zustand/react/shallow';
import {Avatar, Checkbox, Stack} from '@mui/material';
import {designSystemToken, Icon, Label} from '@lightricks/react-design-system';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import {
  CONTENT_STUDIO_VIEW_NAMES,
  FLOW_NAMES,
} from '@/lib/analytics/analyticsConstants';
import {formattedUnixTimestamp} from '@/utils/formattedDate';
import translate from '@/utils/translate';
import {SHORT_MONTH_SHORT_DAY_COMMA_YEAR_DAY_HOUR_MINUTE_FORMAT} from '@/config/timeFormats';
import Table from '@/components/table';
import contentStudioStore, {SelectedAsset} from '@/stores/contentStudioStore';
import ThumbnailDisplay from '../file-upload/ThumbnailDisplay';
import {Asset, AssetCategory} from './types';

dayjs.extend(advancedFormat);

type TableClasses = {
  container?: string;
  paper?: string;
  footer?: string;
  tableHeadRow?: string;
  tableBodyRow?: string;
};

type AssetTableProps = {
  data: Asset[];
  selectedAssets: SelectedAsset[];
  fetchNextPage: () => void;
  isLoading: boolean;
  isFetching: boolean;
  setSelectedAssets: (assets: SelectedAsset[]) => void;
  setQuerySorting: (sorting: SortingState) => void;
  isResults?: boolean;
  classes?: TableClasses;
  sorting?: SortingState;
  isMultiSelect?: boolean;
  contentType?: AssetCategory;
  openPreview?: (assetId: string) => void;
  emptyStateComponent?: React.ReactNode;
  selectedOnlyMode: boolean;
};

const COLUMN_DEFAULT_SORTING = [
  'name',
  'type',
  'project',
  'format',
  'language',
  'source',
  'created',
  'uploaded',
];

function InProgressAssetName({
  name,
  type,
  progress,
}: {
  name: string;
  type: 'upload' | 'result';
  progress?: number;
}) {
  const getProgressText = () => {
    if (!progress) return '';
    if (progress === 100) {
      return `${progress}% uploaded - Analyzing...`;
    }
    return `${progress}% uploaded`;
  };

  return type === 'upload' ? (
    <Stack direction="column">
      <Label
        size="md"
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {name}
      </Label>
      <Label size="sm" color={designSystemToken('semantic.fg.secondary')}>
        {getProgressText()}
      </Label>
    </Stack>
  ) : (
    <Label
      size="md"
      style={{
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
    >
      {`Processing ${name}`}
    </Label>
  );
}

function AssetTable({
  data,
  isLoading,
  isFetching,
  fetchNextPage,
  isResults,
  selectedAssets,
  setSelectedAssets,
  classes,
  setQuerySorting,
  sorting,
  isMultiSelect = true,
  contentType,
  openPreview,
  emptyStateComponent,
  selectedOnlyMode,
}: AssetTableProps) {
  const {uiInteraction} = useUiInteractionEvent();
  const [sortingState, setSortingState] = useState<SortingState>(sorting || []);
  const {files} = contentStudioStore(
    useShallow((state) => ({
      files: state.files,
    }))
  );
  const selectedAssetsByType = selectedOnlyMode
    ? selectedAssets
    : selectedAssets.filter(
        (asset) => (asset.type as AssetCategory) === contentType
      );
  const handleSortingChange = (newSorting: SortingState) => {
    setSortingState(newSorting);
    setQuerySorting(newSorting);
  };
  const checkAsset = (assetId: string) => {
    const asset = data.find((a) => a.id === assetId);
    if (asset) {
      if (selectedAssetsByType.some((a) => a.id === asset.id)) {
        setSelectedAssets(selectedAssets.filter((a) => a.id !== asset.id));
      } else {
        setSelectedAssets([
          ...selectedAssets,
          {
            id: asset.id,
            thumbnail: asset.thumbnailUrl || '',
            isUploading: asset.status !== 'ready',
            type: contentType as 'Uploads' | 'Results',
            url: asset.url,
            fileName: asset.name,
            durationMillis: asset.metadata.durationMillis,
            aspectRatio: asset.metadata.aspectRatio,
          },
        ]);
      }
    }
  };
  const checkAssetSingleSelect = (assetId: string) => {
    const asset = data.find((a) => a.id === assetId);
    if (asset) {
      if (selectedAssetsByType.some((a) => a.id === asset.id)) {
        setSelectedAssets([]);
      } else {
        setSelectedAssets([
          {
            id: asset.id,
            thumbnail: asset.thumbnailUrl || '',
            isUploading: asset.status !== 'ready',
            type: contentType as 'Uploads' | 'Results',
            url: asset.url,
            fileName: asset.name,
            durationMillis: asset.metadata.durationMillis,
            aspectRatio: asset.metadata.aspectRatio,
          },
        ]);
      }
    }
  };
  const uploadTableColumns: MRT_ColumnDef<Asset & {progress?: number}>[] = [
    {
      accessorKey: 'name',
      header: translate('name'),
      minSize: 300,
      Header: () => {
        return (
          <Stack direction="row" alignItems="center" height="18px" gap="32px">
            {isMultiSelect && !selectedOnlyMode && (
              <Checkbox
                onClick={(e) => e.stopPropagation()}
                sx={{height: '18px', width: '18px'}}
                checked={selectedAssetsByType.length === data.length}
                onChange={() => {
                  if (selectedAssetsByType.length === data.length) {
                    setSelectedAssets(
                      selectedAssets.filter(
                        (a) => a.type !== (contentType as unknown)
                      )
                    );
                  } else {
                    setSelectedAssets([
                      ...selectedAssets.filter(
                        (a) => a.type !== (contentType as unknown)
                      ),
                      ...data.map((a) => ({
                        id: a.id,
                        thumbnail: a.thumbnailUrl || '',
                        isUploading: a.status !== 'ready',
                        type: contentType as 'Uploads' | 'Results',
                        url: a.url,
                        fileName: a.name,
                        durationMillis: a.metadata.durationMillis,
                        aspectRatio: a.metadata.aspectRatio,
                      })),
                    ]);
                  }
                }}
              />
            )}
            {isMultiSelect && selectedOnlyMode && (
              <div style={{height: '18px', width: '18px'}} />
            )}
            {translate('name')}
          </Stack>
        );
      },
      Cell: ({row}: {row: MRT_Row<Asset & {progress?: number}>}) => {
        return (
          <Stack
            direction="row"
            alignItems="center"
            gap="32px"
            paddingY="7px"
            sx={{width: '100%', maxWidth: '100%'}}
          >
            {isMultiSelect && (
              <div onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  sx={{height: '18px', width: '18px'}}
                  checked={selectedAssetsByType.some(
                    (a) => a.id === row.original.id
                  )}
                  onChange={(e) => {
                    e.stopPropagation();
                    checkAsset(row.original.id);
                    uiInteraction({
                      item_name: 'check_asset',
                      interaction_location: CONTENT_STUDIO_VIEW_NAMES.ASSETS,
                      asset_id: row.original.id,
                      flow_name: FLOW_NAMES.CONTENT_STUDIO.ASSETS,
                    });
                  }}
                />
              </div>
            )}
            <Stack
              direction="row"
              alignItems="center"
              gap="32px"
              onClick={(e) => {
                e.stopPropagation();
                openPreview?.(row.original.id);
              }}
              sx={{position: 'relative'}}
            >
              {row.original.status === 'in_progress' ? (
                <ThumbnailDisplay
                  file={files[row.original.name]}
                  width={64}
                  height={64}
                />
              ) : (
                <Avatar
                  variant="square"
                  sx={{
                    width: '64px',
                    height: '64px',
                    borderRadius: '4px',
                  }}
                  src={row.original.thumbnailUrl}
                >
                  {' '}
                </Avatar>
              )}
              <Icon
                size="medium"
                appearance="primary"
                name="Actions-Play"
                color={designSystemToken('semantic.bg.primary')}
                style={{
                  position: 'absolute',
                  top: '20px',
                  left: '20px',
                }}
              />
            </Stack>
            <div style={{flex: 1, minWidth: 0, overflow: 'hidden'}}>
              {row.original.status === 'in_progress' ? (
                <InProgressAssetName
                  name={row.original.name}
                  type={row.original.type || 'upload'}
                  progress={row.original?.progress}
                />
              ) : (
                <Label
                  size="md"
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {row.original.name}
                </Label>
              )}
            </div>
          </Stack>
        );
      },
    },
    {
      header: translate('Aspect ratio'),
      id: 'format',
      accessorKey: 'format',
      Cell: ({row}: {row: MRT_Row<Asset>}) => {
        const format = row.original.metadata.aspectRatio
          ? row.original.metadata.aspectRatio
          : '-';
        return <div>{format}</div>;
      },
    },
    {
      header: translate('language'),
      accessorKey: 'language',
      Cell: ({row}: {row: MRT_Row<Asset>}) => {
        return (
          <div>
            {row.original.metadata.language
              ? row.original.metadata.language || '-'
              : '-'}
          </div>
        );
      },
    },
    {
      header: isResults ? translate('created') : translate('uploaded'),
      accessorKey: 'created_at',
      Cell: ({row}: {row: MRT_Row<Asset>}) => {
        return (
          <div>
            {row.original.createdAt
              ? formattedUnixTimestamp(
                  row.original.createdAt,
                  SHORT_MONTH_SHORT_DAY_COMMA_YEAR_DAY_HOUR_MINUTE_FORMAT
                )
              : '-'}
          </div>
        );
      },
    },
  ];

  const resultsTableColumns: MRT_ColumnDef<Asset>[] = [
    ...uploadTableColumns,
    {
      header: translate('source'),
      id: 'source',
      accessorKey: 'source',
      Cell: ({row}: {row: MRT_Row<Asset>}) => {
        return <div>{row.original.originalAsset?.name || '-'}</div>;
      },
    },
    {
      header: translate('Project'),
      id: 'project',
      accessorKey: 'project',
      Cell: ({row}: {row: MRT_Row<Asset>}) => {
        return <div>{row.original.folderName || '-'}</div>;
      },
    },
  ];

  const selectedTableColumns: MRT_ColumnDef<Asset>[] = [
    ...resultsTableColumns,
    {
      header: translate('Type'),
      accessorKey: 'type',
      Cell: ({row}: {row: MRT_Row<Asset>}) => {
        return (
          row.original.type && (
            <div>
              {row.original.type.charAt(0).toUpperCase() +
                row.original.type.slice(1)}
            </div>
          )
        );
      },
    },
  ];

  const tableColumnsMap = {
    [AssetCategory.RESULTS]: resultsTableColumns,
    [AssetCategory.UPLOADS]: uploadTableColumns,
  };
  const columns = selectedOnlyMode
    ? selectedTableColumns
    : tableColumnsMap[contentType ?? AssetCategory.UPLOADS];

  const selectedAssetsMap = Object.fromEntries(
    selectedAssets.map((asset) => [asset.id, true])
  );

  useEffect(() => {
    if (!isMultiSelect && selectedAssets.length > 1) {
      setSelectedAssets(selectedAssets.slice(0, 1));
    }
  }, []);

  return (
    <Table
      columns={columns}
      data={data}
      isLoadingInitial={isLoading}
      showProgressBars={false}
      visibleColumns={{}}
      rowSelection={selectedAssetsMap}
      onSorting={handleSortingChange}
      sorting={sortingState}
      isLoading={isFetching}
      onEndReached={() => {
        fetchNextPage();
      }}
      onRowClick={(row) => {
        // eslint-disable-next-line react/prop-types
        const asset = data.find((a) => a.id === row.id);
        uiInteraction({
          item_name: 'check_asset',
          interaction_location: CONTENT_STUDIO_VIEW_NAMES.ASSETS,
          asset_id: asset?.id,
          flow_name: FLOW_NAMES.CONTENT_STUDIO.ASSETS,
        });
        if (isMultiSelect) {
          // eslint-disable-next-line react/prop-types
          checkAsset(row.id);
        } else {
          // eslint-disable-next-line react/prop-types
          checkAssetSingleSelect(row.id);
        }
      }}
      enableVirtualization
      classes={classes}
      isFirstRowBorder={false}
      columnOrder={COLUMN_DEFAULT_SORTING}
      selectedRowBackgroundColor={designSystemToken('semantic.bg.alpha')}
      emptyStateComponent={emptyStateComponent}
    />
  );
}

export default AssetTable;
